import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { SelectedOption } from '../types';
import { formatOptionName, formatPrice } from '../utils/format';

interface QuotePdfTemplateProps {
  selections: SelectedOption[];
  totalPrice: number;
  quoteNumber: string;
  modelName?: string;
  isKo?: boolean;
}

const QuotePdfTemplate = forwardRef<HTMLDivElement, QuotePdfTemplateProps>(
  ({ selections, totalPrice, quoteNumber, modelName, isKo = true }, ref) => {
    const today = new Date();
    const dateStr = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;

    const stepLabel = (sel: SelectedOption): string =>
      isKo ? sel.step.step_name : (sel.step.step_name_en || sel.step.step_name);
    const optionLabel = (sel: SelectedOption): string =>
      formatOptionName(isKo ? sel.option.option_name : (sel.option.option_name_en || sel.option.option_name));

    const totalText =
      totalPrice > 0
        ? formatPrice(totalPrice, isKo ? 'ko' : 'en')
        : (isKo ? '가격 문의' : 'Contact for price');

    return (
      <Wrapper ref={ref}>
        <HeaderArea>
          <CompanyName>{isKo ? '주식회사 우수건설기계' : 'WOOSU Co., Ltd.'}</CompanyName>
          <DocTitle>{isKo ? '견 적 서' : 'QUOTATION'}</DocTitle>
          <DateText>{isKo ? `발행일: ${dateStr}` : `Issued: ${dateStr}`}</DateText>
        </HeaderArea>

        <Divider />

        <InfoSection>
          <InfoRow>
            <InfoLabel>{isKo ? '견적 번호' : 'Quote No.'}</InfoLabel>
            <InfoValue>{quoteNumber}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{isKo ? '유효 기간' : 'Valid for'}</InfoLabel>
            <InfoValue>{isKo ? '발행일로부터 30일' : '30 days from issue date'}</InfoValue>
          </InfoRow>
        </InfoSection>

        <TableSection>
          <SectionTitle>{isKo ? '구성 옵션 상세' : 'Configuration Details'}{modelName ? <ModelName> ({modelName})</ModelName> : null}</SectionTitle>
          <Table>
            <thead>
              <Tr>
                <Th $width="40px">No.</Th>
                <Th $width="140px">{isKo ? '구분' : 'Category'}</Th>
                <Th $align="right">{isKo ? '선택 옵션' : 'Selected Option'}</Th>
              </Tr>
            </thead>
            <tbody>
              {selections.map((sel, idx) => (
                <Tr key={sel.step.id}>
                  <Td $align="center">{idx + 1}</Td>
                  <Td>{stepLabel(sel)}</Td>
                  <Td $align="right">{optionLabel(sel)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableSection>

        <TotalSection>
          <TotalRow>
            <TotalLabel>{isKo ? '합계 금액 (VAT 포함)' : 'Total (incl. VAT)'}</TotalLabel>
            <TotalValue>{totalText}</TotalValue>
          </TotalRow>
        </TotalSection>

        <FooterArea>
          <FooterNotice>{isKo ? '본 견적서는 참고용이며, 실제 구매 가격은 상담 후 확정됩니다.' : 'This quote is for reference; the final price is confirmed after consultation.'}</FooterNotice>
          <ContactGrid>
            {isKo && (
              <ContactItem>
                <ContactLabel>고객센터</ContactLabel>
                <ContactValue>1522-1609</ContactValue>
              </ContactItem>
            )}
            <ContactItem>
              <ContactLabel>{isKo ? '휴대폰' : 'Mobile'}</ContactLabel>
              <ContactValue>{isKo ? '010-3245-3571' : '+82-10-3245-3571'}</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>{isKo ? 'FAX' : 'Fax'}</ContactLabel>
              <ContactValue>{isKo ? '062-523-1609' : '+82-62-523-1609'}</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>{isKo ? '이메일' : 'Email'}</ContactLabel>
              <ContactValue>top35789@naver.com</ContactValue>
            </ContactItem>
            {isKo && (
              <ContactItem>
                <ContactLabel>카카오톡</ContactLabel>
                <ContactValue>top094989</ContactValue>
              </ContactItem>
            )}
            {isKo && (
              <ContactItem>
                <ContactLabel>상담시간</ContactLabel>
                <ContactValue>월~토 08:00~20:00</ContactValue>
              </ContactItem>
            )}
          </ContactGrid>
          <AddressText>{isKo ? '광주광역시 서구 하남대로502번길 20-11 (덕흥동)' : '20-11, Hanam-daero 502beon-gil, Seo-gu, Gwangju, Republic of Korea'}</AddressText>
          {isKo && <SiteText>https://우수건설기계.com/</SiteText>}
        </FooterArea>
      </Wrapper>
    );
  }
);

QuotePdfTemplate.displayName = 'QuotePdfTemplate';

const Wrapper = styled.div`
  display: none;
  position: absolute;
  left: -9999px;
  top: 0;
  width: 794px;
  padding: 60px;
  background: #fff;
  font-family: 'Pretendard', -apple-system, sans-serif;
  color: #1a1a1a;
`;

const HeaderArea = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const CompanyName = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
`;

const DocTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 12px;
  margin-bottom: 12px;
  color: #1a1a1a;
`;

const DateText = styled.div`
  font-size: 13px;
  color: #888;
`;

const Divider = styled.hr`
  border: none;
  border-top: 2px solid #1a1a1a;
  margin: 24px 0;
`;

const InfoSection = styled.div`
  margin-bottom: 32px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
`;

const InfoLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  min-width: 80px;
`;

const InfoValue = styled.span`
  font-size: 13px;
  color: #333;
`;

const TableSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1a1a1a;
`;

const ModelName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #F5A623;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Tr = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const Th = styled.th<{ $width?: string; $align?: string; $indent?: boolean }>`
  padding: 10px 12px;
  ${({ $indent }) => $indent && 'padding-left: 32px;'}
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: #333;
  text-align: ${({ $align }) => $align || 'left'};
  width: ${({ $width }) => $width || 'auto'};
`;

const Td = styled.td<{ $align?: string; $indent?: boolean }>`
  padding: 10px 12px;
  ${({ $indent }) => $indent && 'padding-left: 32px;'}
  font-size: 13px;
  color: #333;
  text-align: ${({ $align }) => $align || 'left'};
`;

const TotalSection = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 40px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
`;

const TotalValue = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
`;

const FooterArea = styled.div`
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const FooterNotice = styled.p`
  font-size: 12px;
  color: #999;
  text-align: center;
  margin-bottom: 20px;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px 24px;
  margin-bottom: 16px;
`;

const ContactItem = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
`;

const ContactLabel = styled.span`
  color: #999;
  min-width: 56px;
`;

const ContactValue = styled.span`
  color: #555;
  font-weight: 500;
`;

const AddressText = styled.p`
  font-size: 11px;
  color: #999;
  text-align: center;
  margin-bottom: 4px;
`;

const SiteText = styled.p`
  font-size: 11px;
  color: #666;
  text-align: center;
  font-weight: 500;
`;

export default QuotePdfTemplate;
